import { ref, watchEffect } from 'vue'
import { db } from '../firebase/config'
import { collection, query, where, getDocs, getDoc, doc } from "firebase/firestore";

const error = ref(null)

export const getCollection = (collection) => {

  const documents = ref(null)
  const error = ref(null)

  // register the firestore collection reference
  let collectionRef = db.collection(collection)
    .orderBy('createdAt')

  const unsub = collectionRef.onSnapshot(snap => {
    let results = []
    snap.docs.forEach(doc => {
      // must wait for the server to create the timestamp & send it back
      doc.data().createdAt && results.push({ ...doc.data(), id: doc.id })
    })

    // update values
    documents.value = results
    error.value = null
  }, err => {
    console.log(err.message)
    documents.value = null
    error.value = 'could not fetch the data'
  })

  watchEffect((onInvalidate) => {
    onInvalidate(() => unsub())
  })

  return { error, documents }
}

export const fetchDataForUser = async (requestId, date = null, includePastDates = false) => {
  error.value = null

  const currentDate = new Date();
  const currentDateStr = currentDate.toISOString().split('T')[0];

  try {
    const docRef = doc(db, 'volunteer-requests', requestId);
    const documentSnapshot = await getDoc(docRef);
    if (!documentSnapshot.exists()) {
      return [];
    }
    const creatorId = documentSnapshot.data().creator_id;
    if (!creatorId) {
      return [];
    }
    const creatorRef = doc(db, 'users', creatorId);
    const creatorDoc = await getDoc(creatorRef);
    if (creatorDoc.exists() && creatorDoc.data()?.status === 'blocked') {
      return [];
    }
    // Include past dates if requested (for my-requests view), otherwise filter to current/future dates only
    const docData = documentSnapshot.data();
    const dateCheck = includePastDates || (docData.date && docData.date >= currentDateStr);
    if (documentSnapshot.exists() && dateCheck) {
      const data = documentSnapshot.data();
      // Check if the subcollection 'dates' exists
      const datesCollectionRef = collection(docRef, 'dates');
      const datesQuerySnapshot = await getDocs(datesCollectionRef);

      if (!datesQuerySnapshot.empty) {
        // If the subcollection exists, fetch its data and add it to the main document
        const datesData = [];
        datesQuerySnapshot.forEach((doc) => {
          datesData.push(doc.data());
        });

        data.dates = datesData; // Add the 'dates' subcollection data to the main document
      }
      return [data]; // Wrap the result in an array
    } else {
      // console.log("Document not found");
      return [];
    }
  } catch (err) {
    console.error(err.message);
    error.value = 'could not send query result for my volunteering:';
    return error;
  }
}

export const fetchMyVolunteeringSub = async (collectionName, userId, date = null, includeCanceled = false) => {
  error.value = null

  try {
    let q;
    if (includeCanceled) {
      q = query(collection(db, `users/${userId}/${collectionName}`));
    } else {
      q = query(collection(db, `users/${userId}/${collectionName}`), where("status", "!=", "canceled"));
    }
    const querySnapshot = await getDocs(q);
    const newQueryRes = querySnapshot.docs.map(doc => doc.data());

    return newQueryRes;
  }
  catch (err) {
    console.log(err.message)
    error.value = 'could not send query result for my volunteering:'
    return error
  }
}

export const fetchCombinedData = async (collectionName, userId, date = null, includeCanceled = false) => {
  error.value = null;

  try {
    const myVolunteeringData = await fetchMyVolunteeringSub(collectionName, userId, date, includeCanceled);

    const dataPromises = myVolunteeringData.map(async (item) => {
      // Skip items without a valid requestId
      if (!item || !item.requestId) {
        return null;
      }
      // Include past dates when includeCanceled is true (for my-requests view)
      const volunteeringRequestsData = await fetchDataForUser(item.requestId, date, includeCanceled);
      if (Array.isArray(volunteeringRequestsData) && volunteeringRequestsData.length > 0) {
        const modifiedVolunteeringRequestsData = volunteeringRequestsData.map(obj => ({
          ...obj,
          myRecordUid: item.uid,
          unseenUpdate: item.unseenUpdate,
          // Include status from my-requests subcollection if not present in volunteer-requests
          status: obj.status || item.status || ''
        }));

        return modifiedVolunteeringRequestsData[0]
      } else {
        return null;
      }

    });

    const combinedData = await Promise.all(dataPromises);
    const filteredCombinedData = combinedData.filter((data) => data != null);

    return [filteredCombinedData, myVolunteeringData];
  } catch (err) {
    console.error(err.message);
    error.value = 'Could not fetch combined data';
    return error;
  }
};

